<template>
    <web
        :title="tour.seo_title ?? tour.name"
        :meta_description="tour.meta_description"
        :tour="tour"
        :location="location"
        :show-header="true"
        :show-footer="true"
        :show-breadcrumb="true"
        header-variant-setting="tour_display_header_variant"
        header-display-setting="tour_display_header"
        offset-overlay-header
    >
        <template #header>
            <div class="py-2">
                <div class="container mx-auto">
                    <a
                        v-if="backHref"
                        class="text-sm font-semibold text-primary"
                        :href="backHref"
                        @click="onBackClick"
                    >
                        <div class="py-2">
                            <span
                                style="font-size: 12px"
                                class="mr-2 pi pi-arrow-left"
                            />
                            <span>Back</span>
                        </div>
                    </a>
                </div>
                <image-grid
                    :images="tour.images"
                    :alt="tour.name"
                    :video="tour.videos.length > 0 ? tour.videos[0] : undefined"
                />
            </div>
        </template>
        <div
            class="container px-2 mx-auto md:px-10 lg:px-20 py-3 tour-details-container"
        >
            <div class="grid grid-cols-3">
                <div
                    class="flex flex-col w-full col-span-3 gap-5 lg:col-span-2 lg:w-5/6"
                >
                    <div class="flex flex-col gap-1">
                        <div class="flex flex-col">
                            <div class="text-sm uppercase">
                                {{ tour.location[0].address }}
                            </div>
                            <h1 class="!font-bold body-text m-0 h4">
                                {{ reseller_product?.name ?? tour.name }}
                            </h1>
                            <rating
                                v-if="tour.average_rating"
                                :rating="tour.average_rating"
                            />
                            <promotions :tour="tour" inline class="mb-1 mt-2" />
                            <div class="flex-col hidden text-xs lg:flex">
                                <div class="uppercase">Starting From</div>
                                <div class="flex items-center gap-2">
                                    <span
                                        v-if="headlinePricing"
                                        class="text-base text-gray-400 line-through"
                                        >{{
                                            numeral(
                                                headlinePricing.original,
                                            ).format("$0,0.00")
                                        }}</span
                                    >
                                    <span
                                        class="text-2xl font-semibold text-primary"
                                        >{{
                                            numeral(
                                                headlinePricing?.price ??
                                                    reseller_product?.price ??
                                                    tour.price,
                                            ).format("$0,0.00")
                                        }}</span
                                    >
                                    <div class="text-xs">
                                        {{ tour.pricing_type }}
                                    </div>
                                    <i
                                        v-if="headlinePricing?.terms"
                                        v-tooltip.top="headlinePricing.terms"
                                        class="pi pi-info-circle cursor-help text-gray-400"
                                        style="font-size: 0.8rem"
                                    />
                                </div>
                            </div>
                        </div>

                        <div
                            v-if="tour.date_settings?.duration"
                            class="flex items-center text-sm"
                        >
                            <span class="pi pi-clock" />
                            <span class="px-2">{{
                                tour.date_settings.duration
                            }}</span>
                        </div>

                        <div
                            class="flex flex-col gap-2 py-3 border-dashed border-y-1"
                        >
                            <div class="font-semibold">Includes:</div>
                            <div class="flex flex-wrap gap-2">
                                <div
                                    class="flex flex-wrap items-center justify-center"
                                    v-for="tag in tour.inclusions"
                                >
                                    <span :class="'fa ' + tag.icon" />
                                    <span class="px-2">{{ tag.name }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col gap-2">
                        <h2 class="!font-bold body-text">Description</h2>
                        <div class="content-divider" />
                        <strong>{{
                            reseller_product?.shortDescription ??
                            tour.short_description
                        }}</strong>

                        <div
                            v-html="reseller_product?.overview ?? tour.overview"
                        />
                    </div>

                    <div class="flex flex-col gap-2">
                        <h2 class="!font-bold body-text">Tour Highlights</h2>
                        <div class="content-divider" />
                        <div
                            class="highlights"
                            v-html="
                                reseller_product?.highlights ?? tour.highlights
                            "
                        />
                    </div>

                    <div>
                        <accordion multiple>
                            <accordion-panel
                                as="div"
                                :value="data._id"
                                v-for="data in tour.information"
                            >
                                <accordion-header as="button">
                                    <h2 class="body-text !font-bold">
                                        {{ data.name }}
                                    </h2>
                                </accordion-header>

                                <accordion-content as="div">
                                    <div class="content-divider" />
                                    <div v-html="data.content" />
                                </accordion-content>
                            </accordion-panel>

                            <accordion-panel
                                as="div"
                                v-if="getSetting('cancellation_policy')"
                                value="cancellation_policy"
                            >
                                <accordion-header as="button">
                                    <h2 class="body-text !font-bold">
                                        Terms and Conditions
                                    </h2>
                                </accordion-header>

                                <accordion-content as="div">
                                    <div class="content-divider" />
                                    <content-block
                                        :id="getSetting('cancellation_policy')"
                                    />
                                </accordion-content>
                            </accordion-panel>
                        </accordion>
                    </div>

                    <div>
                        <h2 class="!font-bold body-text">Location</h2>

                        <div class="mb-1">
                            {{ tour.location[0].address }}
                        </div>

                        <iframe
                            width="100%"
                            height="450"
                            class="border-none"
                            :title="`Map showing the location of ${tour.name}`"
                            :src="`https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API}&q=${tour.location[0].address}`"
                        />
                    </div>
                </div>

                <div class="hidden lg:block">
                    <div
                        id="booking"
                        ref="bookingComponent"
                        class="scroll-offset"
                    >
                        <div
                            class="pl-5 border-l my-10"
                            :style="{
                                borderColor: colors.primary,
                            }"
                            v-if="specific"
                        >
                            <h3 class="!font-bold body-text">Book Now</h3>
                            <p>
                                Contact or Visit Your Tour Desk Representative
                                For More Information.
                            </p>
                        </div>
                        <booking
                            v-else
                            :tour="tour"
                            :reseller-product="reseller_product"
                        />
                    </div>
                    <transition name="fade-slide">
                        <div
                            v-if="showComponent"
                            class="sticky topbar-offset w-full py-5 flex flex-col gap-5"
                        >
                            <div
                                class="!font-bold body-text m-0"
                                style="line-height: 1em"
                            >
                                {{ tour.name }}
                            </div>
                            <rating
                                v-if="tour.average_rating"
                                :rating="tour.average_rating"
                            />
                            <div class="flex justify-between">
                                <div
                                    class="flex-col flex text-xs"
                                    style="line-height: 0.5em"
                                >
                                    <div class="uppercase text-nowrap">
                                        Starting From
                                    </div>
                                    <div class="flex items-center gap-2">
                                        <span
                                            v-if="stickyPricing"
                                            class="text-base text-gray-400 line-through"
                                            >{{
                                                numeral(
                                                    stickyPricing.original,
                                                ).format("$0,0.00")
                                            }}</span
                                        >
                                        <span class="text-2xl font-semibold">{{
                                            numeral(
                                                stickyPricing?.price ??
                                                    tour.price,
                                            ).format("$0,0.00")
                                        }}</span>
                                        <i
                                            v-if="stickyPricing?.terms"
                                            v-tooltip.top="stickyPricing.terms"
                                            class="pi pi-info-circle cursor-help text-gray-400"
                                            style="font-size: 0.75rem"
                                        />
                                    </div>
                                    <div class="text-xs">
                                        {{ tour.pricing_type }}
                                    </div>
                                </div>

                                <p-button
                                    @click="scrollToAnchor('booking')"
                                    class="px-5 py-2"
                                    label="Book Now"
                                    rounded
                                    size="small"
                                />
                            </div>
                        </div>
                    </transition>
                </div>
            </div>
            <div class="pt-5">
                <review-component :tour="tour" />
            </div>

            <deferred :data="['similar_tours']">
                <template #fallback>
                    <div class="flex items-center justify-center w-full p-20">
                        <progress-bar
                            class="w-[50vw] lg:w-[25vw]"
                            mode="indeterminate"
                            style="height: 2px"
                        />
                    </div>
                </template>
                <div
                    v-if="similar_tours && similar_tours.length > 0"
                    class="py-5 flex flex-col gap-5"
                >
                    <h2 class="!font-bold body-text text-center">
                        You might also like...
                    </h2>
                    <div>
                        <similar-tours
                            layout="scroll"
                            :tours="similar_tours"
                            :data="{}"
                            fixedlayout
                            :country="location?.country_slug"
                            :region="location?.region_slug"
                        />
                    </div>
                </div>
            </deferred>
        </div>

        <div
            class="fixed gap-10 left-0 right-0 bottom-0 p-3 flex lg:hidden justify-between items-center bg-white shadow-sm z-10"
        >
            <div class="flex-col flex text-xs" style="line-height: 0.5em">
                <div class="uppercase text-nowrap">Starting From</div>
                <div class="flex items-center gap-2">
                    <span
                        v-if="stickyPricing"
                        class="text-base text-gray-400 line-through"
                        >{{
                            numeral(stickyPricing.original).format("$0,0.00")
                        }}</span
                    >
                    <span class="text-2xl font-semibold">{{
                        numeral(stickyPricing?.price ?? tour.price).format(
                            "$0,0.00",
                        )
                    }}</span>
                    <i
                        v-if="stickyPricing?.terms"
                        v-tooltip.top="stickyPricing.terms"
                        class="pi pi-info-circle cursor-help text-gray-400"
                        style="font-size: 0.75rem"
                    />
                </div>
                <div class="text-xs">
                    {{ tour.pricing_type }}
                </div>
            </div>

            <div class="w-3/4 md:w-1/2">
                <div
                    v-if="specific"
                    class="pl-5 border-l my-10"
                    :style="{
                        borderColor: colors.primary,
                    }"
                >
                    <h3 class="!font-bold body-text">Book Now</h3>
                    <p>
                        Contact or Visit Your Tour Desk Representative For More
                        Information.
                    </p>
                </div>

                <p-button
                    v-else-if="tour.call_to_book"
                    a="a"
                    :href="`tel:${getSetting(
                        'phone_number',
                        '1-877-549-4821',
                    )}`"
                    fluid
                    size="large"
                    rounded
                >
                    <div class="flex items-center flex-col justify-center">
                        <span class="font-semibold">Book by phone</span>
                        <span class="text-sm">
                            {{ getSetting("phone_number", "1-877-549-4821") }}
                        </span>
                    </div>
                </p-button>

                <p-button
                    v-else-if="tour.email_to_book"
                    size="large"
                    class="px-5 py-3 email_to_book"
                    fluid
                    rounded
                    >{{
                        getSetting("email_to_book_btn_cta", "Book by Email")
                    }}</p-button
                >

                <resmark-booking
                    v-else-if="hasAgency"
                    :product-code="
                        reseller_product?.productCode ?? tour.product_code
                    "
                    id="mobile-booking"
                />

                <p-button
                    v-else
                    @click="book = !book && !inCart(tour.id + '')"
                    :as="inCart(tour.id + '') ? Link : undefined"
                    :href="inCart(tour.id + '') ? route('cart') : undefined"
                    fluid
                    size="large"
                    rounded
                >
                    <div class="flex gap-3 items-center">
                        <span
                            v-if="inCart(tour.id + '')"
                            class="pi pi-check-circle"
                        />
                        <span class="text-sm">
                            {{ inCart(tour.id + "") ? "In Cart" : "Book Now" }}
                        </span>
                    </div>
                </p-button>
            </div>
        </div>
        <p-dialog
            v-if="!hasAgency"
            v-model:visible="book"
            modal
            header=" "
            class="rounded-0 max-w-[90vw] md:max-w-[60vw] lg:max-w-[30vw]"
            :append-to="getModalContainer()"
        >
            <booking :tour="tour" />
        </p-dialog>

        <email-to-book-modal />
    </web>
</template>

<script setup lang="ts">
import { Tour, Location, ResellerProduct } from "@/types";
import Web from "@/Layouts/Web.vue";
import Booking from "@/Components/Booking.vue";
import {
    Accordion,
    AccordionPanel,
    AccordionContent,
    AccordionHeader,
    Button as PButton,
    Dialog as PDialog,
    ProgressBar,
} from "primevue";
import ImageGrid from "@/Components/ImageGrid.vue";
import Promotions from "@/Components/Tour/Item/Promotions.vue";
import { Deferred, Link, usePage } from "@inertiajs/vue3";
import numeral from "numeral";
import useTheme from "@/Hook/useTheme";
import { computed, onMounted, ref } from "vue";
import useCart from "@/Hook/useCart";
import {
    getGoogleMapsApiKey,
    getModalContainer,
    promoPricing,
} from "@/utils/helpers";
import SimilarTours from "@/Components/Tour/Tours.vue";
import { useSetting } from "@/Hook/useSetting";
import ResmarkBooking from "@/Components/ResmarkBooking.vue";
import useAgency from "@/Hook/useAgency";
import Rating from "@/Components/Reviews/Rating.vue";
import ReviewComponent from "@/Components/Reviews/Index.vue";
import { onUnmounted } from "vue";
import useDataLayer from "@/Hook/useDataLayer";
import EmailToBookModal from "@/Components/EmailToBookModal.vue";
import ContentBlock from "@/Components/ContentBlock.vue";

const { tour, reseller_product, location } = defineProps<{
    location?: Location;
    tour: Tour;
    reseller_product?: ResellerProduct;
    similar_tours?: Tour[];
}>();

const { colors } = useTheme();
const { inCart } = useCart();

// Active promotional pricing (discounted price + strikethrough + terms).
// The headline reflects the (possibly reseller) price shown beside it; the
// sticky/mobile bars always use the base tour price.
const headlinePricing = computed(() =>
    promoPricing(tour, reseller_product?.price ?? tour.price),
);
const stickyPricing = computed(() => promoPricing(tour));

const book = ref(false);

const specific = usePage().props?.specific_tours;

const GOOGLE_MAPS_API = ref(getGoogleMapsApiKey());

const { hasAgency } = useAgency();

const { getSetting } = useSetting();

const { registerTourVisit } = useDataLayer();

const bookingComponent = ref();
const showComponent = ref(false);

const onScroll = () => {
    if (!bookingComponent.value) return;

    const rect = bookingComponent.value.getBoundingClientRect();

    // Show Component B when bottom of Component A is above the top of viewport
    showComponent.value = rect.bottom <= 0;
};

const scrollToAnchor = (id: string) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
};

function computeFallbackBackHref(): string | undefined {
    if (tour.attractions?.length && tour.locations?.length) {
        return route("show.page", {
            page: `visit-${tour.attractions[0].slug}-${tour.locations[0].country_slug}`,
        });
    }
    if (location) {
        return route(specific ? "specific.tours" : "tours", {
            country: location.country_slug,
            region: location.region_slug || undefined,
        });
    }
    return undefined;
}

const backHref = ref<string | undefined>(computeFallbackBackHref());
const useHistoryBack = ref(false);

function onBackClick(e: MouseEvent) {
    if (!useHistoryBack.value) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    window.history.back();
}

onMounted(() => {
    window.addEventListener("scroll", onScroll);
    registerTourVisit(tour, location, reseller_product);

    if (document.referrer) {
        try {
            const ref_url = new URL(document.referrer);
            if (
                ref_url.origin === window.location.origin &&
                ref_url.href !== window.location.href
            ) {
                backHref.value = ref_url.href;
                useHistoryBack.value = true;
            }
        } catch {
            // malformed referrer — keep fallback
        }
    }
});

onUnmounted(() => {
    window.removeEventListener("scroll", onScroll);
});
</script>

<style lang="scss">
a {
    text-decoration: none;
}

.p-accordionpanel {
    border: none !important;
}

.p-accordionheader {
    padding: 10px 0 !important;
    background: none !important;
}

.content-divider {
    border-top: 4px solid v-bind("colors.primary");
    margin-bottom: 10px;
    width: 10%;
}

.p-accordioncontent {
    .p-accordioncontent-content {
        padding-left: 0 !important;
        color: v-bind("colors.body.text_color") !important;

        background: none !important;
    }
}

.p-accordioncontent,
.highlights {
    ul {
        list-style: none;
        padding-left: 0;
    }

    li {
        position: relative;
        padding-left: 25px;
    }

    li::before {
        font-family: "Font Awesome 5 Free";
        content: "\f00c";
        font-weight: 900;
        position: absolute;
        left: 0;
        top: 13px;
        transform: translateY(-50%);
        color: v-bind("colors.primary");
    }
}

.fade-slide-enter-active,
.fade-slide-leave-active {
    transition:
        opacity 0.25s ease,
        transform 0.25s ease;
}

.fade-slide-enter-from {
    opacity: 0;
    transform: translateY(20px);
}

.fade-slide-enter-to {
    opacity: 1;
    transform: translateY(0);
}

.fade-slide-leave-from {
    opacity: 1;
    transform: translateY(0);
}

.fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-20px);
}
</style>
